'use client';

import { ButtonHTMLAttributes, forwardRef } from 'react';
import { motion } from 'framer-motion';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({
        children,
        variant = 'primary',
        size = 'md',
        isLoading = false,
        className = '',
        disabled,
        ...props
    }, ref) => {
        const baseStyles = `
      inline-flex items-center justify-center
      font-medium rounded-xs
      transition-colors duration-200
      hover:cursor-pointer
      focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-slate-900
      disabled:opacity-50 disabled:cursor-not-allowed
    `;

        const variants = {
            primary: `
        bg-black text-white
        hover:bg-slate-800
        border border-transparent
      `,
            secondary: `
        bg-white text-black
        border border-black
        hover:bg-slate-50
      `,
            outline: `
        bg-transparent
        border border-black
        text-black
        hover:bg-black hover:text-white
      `,
            ghost: `
        bg-transparent
        text-slate-600
        hover:text-black hover:underline
      `,
            danger: `
        bg-red-600 text-white
        hover:bg-red-700
      `,
        };

        const sizes = {
            sm: 'px-3 py-1.5 text-sm',
            md: 'px-5 py-2.5 text-base',
            lg: 'px-8 py-3.5 text-lg',
        };

        const isDisabled = disabled || isLoading;

        return (
            <motion.button
                ref={ref}
                disabled={isDisabled}
                className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
                whileTap={!isDisabled ? { scale: 0.98 } : undefined}
                type={props.type || 'button'}
                onClick={props.onClick}
            >
                {isLoading ? (
                    <>
                        <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                        </svg>
                        Loading...
                    </>
                ) : (
                    children
                )}
            </motion.button>
        );
    }
);

Button.displayName = 'Button';
